import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

type CRMType = CRMSupported;
type LoadingState = 'idle' | 'loading' | 'authenticated' | 'unauthenticated';
type InstallState = 'idle' | 'loading' | 'installed' | 'uninstalled' | 'success' | 'error';

interface CRMState {
  installState: InstallState;
  enabled: boolean;
  providerId?: string;
}

export interface ParagonLoadState {
  loadingState: LoadingState;
  crm: Record<CRMType, CRMState> | null;
}

interface CRMInfo {
  crmType: CRMType;
  providerId?: string;
}

export interface ParagonLoadAction {
  setLoadingState: (loadingState: LoadingState) => void;
  setInstallState: (installState: InstallState, crmInfo: CRMInfo) => void;
}

/**
 * Store to manage Paragon Load State
 * Handles authentication state and CRM installation states
 */
const useParagonLoadState = create<ParagonLoadState & ParagonLoadAction>()(
  devtools(
    immer((set) => ({
      loadingState: 'idle',
      crm: null,
      setLoadingState: (loadingState: LoadingState) => set({ loadingState }, false, { type: 'SET_LOADING_STATE' }),
      setInstallState: (installState, crmInfo) =>
        set(
          (state) => {
            // Initialize CRM state if null
            if (!state.crm) {
              state.crm = {
                hubspot: { installState: 'idle', enabled: false },
                salesforce: { installState: 'idle', enabled: false },
                pipedrive: { installState: 'idle', enabled: false },
                salesloft: { installState: 'idle', enabled: false },
                msdynamics365sales: { installState: 'idle', enabled: false },
                jobadder: { installState: 'idle', enabled: false },
              };
            }

            // Update specific CRM state
            if (state.crm) {
              state.crm[crmInfo.crmType] = {
                installState,
                enabled: installState === 'installed' || installState === 'success',
                providerId: crmInfo.providerId,
              };
            }
          },
          false,
          { type: 'SET_INSTALL_STATE' },
        ),
    })),
    {
      store: 'ParagonLoadState',
    },
  ),
);

export { useParagonLoadState };
